import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useEffect } from 'react';
import { getSupabaseClient } from '@lingaai/supabase-client';
import type { User, UserPermissions, UserModule, SystemPermissions } from '../types';

interface AuthStore {
  // State
  user: User | null;
  users: User[] | null;
  permissions: UserPermissions | null;
  modules: UserModule[] | null;
  isLoading: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setUsers: (users: User[] | null) => void;
  setPermissions: (permissions: UserPermissions | null) => void;
  setModules: (modules: UserModule[] | null) => void;
  fetchUsers: (tenantId: string) => Promise<void>;
  updateUserProfile: (userId: string, updates: Partial<User>) => Promise<void>;

  // Helpers
  hasPermission: (path: string) => boolean;
  hasModule: (moduleId: string, minAccess?: 'read' | 'write' | 'full') => boolean;
  hasSystemPermission: (permission: keyof SystemPermissions) => boolean;

  // Lifecycle
  initialize: () => Promise<void>;
  refresh: () => Promise<void>;
  clear: () => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      users: null,
      permissions: null,
      modules: null,
      isLoading: false,

      // Setters
      setUser: (user) => set({ user }),
      setUsers: (users) => set({ users }),
      setPermissions: (permissions) => set({ permissions }),
      setModules: (modules) => set({ modules }),

      // Fetch all users for tenant
      fetchUsers: async (tenantId) => {
        const supabase = getSupabaseClient();

        const { data, error } = await supabase.from('users').select('*').eq('tenant_id', tenantId).order('created_at', { ascending: false });

        if (error) throw error;

        set({ users: data });
      },

      // Update user profile
      updateUserProfile: async (userId, updates) => {
        const supabase = getSupabaseClient();

        const { data, error } = await supabase.from('users').update(updates).eq('id', userId).select().single();

        if (error) throw error;

        // Update current user if it's the same
        const { user, users } = get();
        if (user?.id === userId) {
          set({ user: data });
        }

        // Update in users array
        if (users) {
          set({
            users: users.map((u) => (u.id === userId ? data : u)),
          });
        }
      },

      // Permission checkers
      hasPermission: (path: string) => {
        const { permissions } = get();
        if (!permissions) return false;

        const parts = path.split('.');
        let value: any = permissions;

        for (const part of parts) {
          value = value?.[part];
          if (value === undefined) return false;
        }

        return Boolean(value);
      },

      hasModule: (moduleId: string, minAccess = 'read') => {
        const { modules } = get();
        if (!modules) return false;

        const module = modules.find((m) => m.module_id === moduleId);
        if (!module) return false;

        const accessLevels = ['none', 'read', 'write', 'full'];
        const userLevel = accessLevels.indexOf(module.access_level);
        const requiredLevel = accessLevels.indexOf(minAccess);

        return userLevel >= requiredLevel;
      },

      hasSystemPermission: (permission) => {
        const { permissions } = get();
        return permissions?.system?.[permission] ?? false;
      },

      // Initialize on app load
      initialize: async () => {
        set({ isLoading: true });

        try {
          const supabase = getSupabaseClient();

          // Get current session
          const {
            data: { user: authUser },
          } = await supabase.auth.getUser();

          if (!authUser) {
            get().clear();
            return;
          }

          // Load user data with tenants relation for module_config
          const { data: userData, error: userError } = await supabase.from('users').select('*, tenants!inner(module_config)').eq('id', authUser.id).single();

          if (userError) {
            console.error('AuthStore: Error loading user data:', userError);
            throw userError;
          }

          set({ user: userData });

          // Load permissions
          const { data: perms, error: permsError } = await supabase.rpc('get_user_effective_permissions', { p_user_id: authUser.id });

          if (permsError) throw permsError;
          set({ permissions: perms });

          // Load modules
          const { data: mods, error: modsError } = await supabase.rpc('get_user_modules', { p_user_id: authUser.id });

          if (modsError) throw modsError;
          set({ modules: mods });

          // Subscribe to permission changes
          supabase
            .channel('user-permissions')
            .on(
              'postgres_changes',
              {
                event: 'UPDATE',
                schema: 'public',
                table: 'users',
                filter: `id=eq.${authUser.id}`,
              },
              () => {
                // Refresh permissions when user record changes
                get().refresh();
              }
            )
            .subscribe();
        } catch (error) {
          console.error('AuthStore: Failed to initialize:', error);
          get().clear();
        } finally {
          set({ isLoading: false });
        }
      },

      // Refresh permissions
      refresh: async () => {
        const { user } = get();
        if (!user) return;

        try {
          const supabase = getSupabaseClient();

          const { data: perms } = await supabase.rpc('get_user_effective_permissions', { p_user_id: user.id });

          const { data: mods } = await supabase.rpc('get_user_modules', { p_user_id: user.id });

          set({ permissions: perms, modules: mods });
        } catch (error) {
          console.error('Failed to refresh permissions:', error);
        }
      },

      // Clear on logout
      clear: () =>
        set({
          user: null,
          permissions: null,
          modules: null,
        }),
    }),
    {
      name: 'linga-auth-storage',
      partialize: (state) => ({
        user: state.user,
        permissions: state.permissions,
        modules: state.modules,
      }),
    }
  )
);

// Hook to initialize auth on app mount
export function useInitAuth() {
  useEffect(() => {
    const initialize = useAuthStore.getState().initialize;
    const clear = useAuthStore.getState().clear;

    initialize();

    const supabase = getSupabaseClient();

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        clear();
      }
    });

    return () => subscription.unsubscribe();
  }, []);
}
